import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MarkdownLite from "../components/MarkdownLite";
import { WhatsAppFab, WhatsAppIcon } from "../components/Extras";
import { useConfig } from "../lib/useConfig";
import { buildWhatsAppUrl } from "../data/content";

export default function BlogPost() {
    const { slug } = useParams();
    const config = useConfig();
    const [post, setPost] = useState(null);
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        setStatus("loading");
        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/api/blog/${slug}`)
            .then((r) => {
                setPost(r.data);
                setStatus("ok");
                document.title = `${r.data.title} | LoloPets Costa Rica`;
                const meta = document.querySelector('meta[name="description"]');
                if (meta && r.data.excerpt) meta.setAttribute("content", r.data.excerpt);
                const ld = document.createElement("script");
                ld.type = "application/ld+json";
                ld.id = "article-jsonld";
                ld.text = JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Article",
                    headline: r.data.title,
                    description: r.data.excerpt || r.data.title,
                    image: r.data.image || undefined,
                    author: { "@type": "Organization", name: "LoloPets Costa Rica" },
                    publisher: { "@type": "Organization", name: "LoloPets Costa Rica" },
                    inLanguage: "es-CR",
                });
                document.head.appendChild(ld);
            })
            .catch(() => setStatus("notfound"));
    }, [slug]);

    return (
        <div data-testid="blog-post-page" className="min-h-screen bg-white">
            <Header base="/" />
            <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 md:py-16">
                {status === "loading" && (
                    <div data-testid="blog-post-loading" className="animate-pulse space-y-4">
                        <div className="h-8 w-2/3 rounded bg-black/[0.05]" />
                        <div className="aspect-[16/9] rounded-2xl bg-black/[0.04]" />
                        <div className="h-4 w-full rounded bg-black/[0.04]" />
                        <div className="h-4 w-5/6 rounded bg-black/[0.04]" />
                    </div>
                )}
                {status === "notfound" && (
                    <div data-testid="blog-post-notfound" className="py-20 text-center">
                        <h1 className="font-display text-3xl font-bold text-brand-ink">Artículo no encontrado</h1>
                        <Link to="/blog" className="mt-6 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand-magenta px-8 py-3 font-display font-semibold text-white">
                            <ArrowLeft className="h-4 w-4" /> Ver todos los consejos
                        </Link>
                    </div>
                )}
                {status === "ok" && post && (
                    <article>
                        <Link
                            data-testid="blog-post-back"
                            to="/blog"
                            className="inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-brand-muted transition-colors hover:text-brand-magenta"
                        >
                            <ArrowLeft className="h-4 w-4" /> Todos los consejos
                        </Link>
                        <h1 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight text-brand-ink sm:text-4xl">
                            {post.title}
                        </h1>
                        {post.excerpt && <p className="mt-4 text-lg leading-relaxed text-brand-muted">{post.excerpt}</p>}
                        {post.image && (
                            <div className="mt-8 overflow-hidden rounded-2xl bg-black/[0.03] shadow-soft">
                                <img src={post.image} alt={post.title} className="w-full object-cover" loading="eager" />
                            </div>
                        )}
                        <div className="mt-10">
                            <MarkdownLite content={post.content} />
                        </div>
                        <div className="mt-12 rounded-2xl bg-brand-ink p-8 text-center">
                            <h2 className="font-display text-xl font-bold text-white">¿Dudas sobre la alimentación de tu mascota?</h2>
                            <p className="mt-2 text-sm text-white/70">
                                Escríbenos y te contamos dónde encontrar LoloPets cerca de ti.
                            </p>
                            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                                <a
                                    data-testid="blog-post-whatsapp"
                                    href={buildWhatsAppUrl(config?.whatsapp, `Hola LoloPets, leí su artículo "${post.title}" y tengo una consulta.`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-brand-magenta px-8 py-3 font-display font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-magentadark"
                                >
                                    <WhatsAppIcon className="h-4 w-4" /> Escribir por WhatsApp
                                </a>
                                <Link
                                    data-testid="blog-post-products"
                                    to="/#productos"
                                    className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border-2 border-white/25 px-8 py-3 font-display font-semibold text-white transition-colors hover:border-white/60"
                                >
                                    Ver productos <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </article>
                )}
            </main>
            <Footer base="/" config={config} />
            <WhatsAppFab url={buildWhatsAppUrl(config?.whatsapp)} />
        </div>
    );
}
